/**
 * Parent/guardian book-in / can't-come reasons for PWA Sessions.
 */

import { nzWallClock, timeToMinutes, type NzWallClock } from './time';

/** Minutes after session start during which book-in may still be changed. */
export const CHECK_IN_CANCEL_GRACE_MINUTES = 15;

export const CHECK_IN_CANCEL_WINDOW_CLOSED_MESSAGE =
  "Can't come can no longer be recorded — more than 15 minutes after session start.";

/**
 * Whether book-in / can't-come may still be changed at `now` (NZ wall-clock).
 * Allowed until session start + CHECK_IN_CANCEL_GRACE_MINUTES (inclusive).
 */
export function canCancelCheckIn(
  sessionDate: string,
  startTime: string,
  now: NzWallClock = nzWallClock(),
): boolean {
  if (now.isoDate < sessionDate) return true;
  if (now.isoDate > sessionDate) return false;
  const cutoff = timeToMinutes(startTime) + CHECK_IN_CANCEL_GRACE_MINUTES;
  return now.totalMinutes <= cutoff;
}

/** Alias used by parent UI for the same window as un-booking. */
export const canNotifyCannotAttend = canCancelCheckIn;

/** Statuses that occupy a lane (advance booking or arrived at the pool). */
export const SESSION_OCCUPYING_STATUSES = ['booked', 'checked_in'] as const;

/** Coach/session roll: occupying + waitlist + can't-come. */
export const SESSION_COACH_ROLL_STATUSES = ['booked', 'checked_in', 'waitlist', 'cancelled'] as const;

export function isOccupyingSessionStatus(status: string | null | undefined): boolean {
  return status === 'booked' || status === 'checked_in';
}

export function isBookedInStatus(status: string | null | undefined): boolean {
  return status === 'booked' || status === 'checked_in' || status === 'waitlist';
}

export function isCoachRollStatus(status: string | null | undefined): boolean {
  return isBookedInStatus(status) || status === 'cancelled';
}

export function isArrivedStatus(status: string | null | undefined): boolean {
  return status === 'checked_in';
}

export function isCannotAttendStatus(status: string | null | undefined): boolean {
  return status === 'cancelled';
}

export function bookInStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'booked':
      return 'Booked';
    case 'checked_in':
      return 'Checked in';
    case 'waitlist':
      return 'Waitlist';
    case 'cancelled':
      return "Can't come";
    case 'no_show':
      return 'No-show';
    default:
      return status ? status.replace(/_/g, ' ') : 'Not booked';
  }
}

/** Minutes before session start when arrival check-in opens. */
export const ARRIVAL_CHECK_IN_BEFORE_MINUTES = 90;
/** Minutes after session end when arrival check-in closes. */
export const ARRIVAL_CHECK_IN_AFTER_END_MINUTES = 30;

/**
 * Whether a booked swimmer may tap “Check in” (I've arrived) at `now`.
 * Open from 90 minutes before start until 30 minutes after end, on the session date.
 */
export function canArrivalCheckIn(
  sessionDate: string,
  startTime: string,
  endTime: string,
  now: NzWallClock = nzWallClock(),
): boolean {
  if (now.isoDate !== sessionDate) return false;
  const open = timeToMinutes(startTime) - ARRIVAL_CHECK_IN_BEFORE_MINUTES;
  const close = timeToMinutes(endTime) + ARRIVAL_CHECK_IN_AFTER_END_MINUTES;
  return now.totalMinutes >= open && now.totalMinutes <= close;
}

export const CHECK_IN_CANCEL_REASONS = [
  { code: 'unwell', label: 'Unwell / sick' },
  { code: 'injury', label: 'Injury' },
  { code: 'away_travel', label: 'Away / travel' },
  { code: 'school_commitment', label: 'School commitment' },
  { code: 'family_commitment', label: 'Family commitment' },
  { code: 'transport', label: 'Transport issue' },
  { code: 'not_usual_session', label: 'Not my usual session' },
  { code: 'other', label: 'Other' },
] as const;

export type CheckInCancelReasonCode = (typeof CHECK_IN_CANCEL_REASONS)[number]['code'];

export const CHECK_IN_CANCEL_REASON_CODES = CHECK_IN_CANCEL_REASONS.map((r) => r.code);

export function isCheckInCancelReasonCode(value: string): value is CheckInCancelReasonCode {
  return (CHECK_IN_CANCEL_REASON_CODES as readonly string[]).includes(value);
}

export function validateCheckInCancelReason(
  reasonCode: string,
  reasonText?: string | null,
): { ok: true; reason_code: CheckInCancelReasonCode; reason_text: string | null } | { ok: false; message: string } {
  if (!isCheckInCancelReasonCode(reasonCode)) {
    return {
      ok: false,
      message: `reason_code must be one of: ${CHECK_IN_CANCEL_REASON_CODES.join(', ')}`,
    };
  }
  const trimmed = (reasonText ?? '').trim();
  if (reasonCode === 'other' && trimmed.length < 2) {
    return { ok: false, message: 'Please describe the reason when choosing Other.' };
  }
  return {
    ok: true,
    reason_code: reasonCode,
    reason_text: trimmed.length > 0 ? trimmed.slice(0, 500) : null,
  };
}

/** Human-readable reason for UI (roster, PWA status). Prefers free-text for `other`. */
export function formatCannotAttendReason(
  reasonCode: string | null | undefined,
  reasonText?: string | null,
): string {
  const trimmed = (reasonText ?? '').trim();
  if (reasonCode === 'other' && trimmed.length > 0) return trimmed;
  const found = CHECK_IN_CANCEL_REASONS.find((r) => r.code === reasonCode);
  if (found) return found.label;
  return trimmed.length > 0 ? trimmed : "Can't come";
}
